import { useState, useRef } from "react";
import { CATEGORIES } from "../utils/constants";
import { uid } from "../utils/helpers";
import styles from "../styles/theme";

export default function BulkAddModal({ people, onSave, onClose }) {
  const emptyRow = () => ({
    _key: uid(),
    name: "",
    relationship: "",
    category: "social",
    parentId: "",
  });
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const nameRefs = useRef({});

  const updateRow = (key, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r._key === key ? { ...r, [field]: value } : r)),
    );
  };

  const removeRow = (key) => {
    setRows((prev) => prev.filter((r) => r._key !== key));
  };

  const addRow = () => {
    const newRow = emptyRow();
    setRows((prev) => [...prev, newRow]);
    // Focus the new row's name field after render
    setTimeout(() => {
      nameRefs.current[newRow._key]?.focus();
    }, 50);
  };

  const validRows = rows.filter((r) => r.name.trim());

  const handleSave = async () => {
    if (validRows.length === 0) return;
    setSaving(true);
    const entries = validRows.map((row) => ({
      ...row,
      name: row.name.trim(),
      relationship: row.relationship.trim(),
    }));
    const result = await onSave(entries);
    setSaving(false);
    setSaved(result.length);
  };

  // Handle Enter key to jump to next row or add new
  const handleKeyDown = (e, key, field) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const idx = rows.findIndex((r) => r._key === key);
      if (field === "name") {
        // Focus relationship of same row
        const relEl = document.getElementById(`bulk-rel-${key}`);
        relEl?.focus();
      } else if (idx === rows.length - 1) {
        addRow();
      } else {
        const nextKey = rows[idx + 1]._key;
        nameRefs.current[nextKey]?.focus();
      }
    }
  };

  // Get linkable people (existing people + already-filled rows)
  const getLinkOptions = (currentKey) => {
    const currentCategory = rows.find((r) => r._key === currentKey)?.category;
    const existingInCat = people.filter((p) => p.category === currentCategory);
    const rowsInCat = rows.filter(
      (r) =>
        r._key !== currentKey &&
        r.name.trim() &&
        r.category === currentCategory,
    );
    return { existing: existingInCat, pending: rowsInCat };
  };

  if (saved !== null) {
    return (
      <div style={styles.modalOverlay} onClick={onClose}>
        <div
          style={{ ...styles.modal, maxWidth: 420 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <h2 style={{ ...styles.modalTitle, marginBottom: 8 }}>
              Added {saved} {saved === 1 ? "Person" : "People"}!
            </h2>
            <p style={{ color: "#7a7a7e", fontSize: 14, marginBottom: 24 }}>
              Your network just grew. View them in the People list or Tree view.
            </p>
            <button onClick={onClose} style={styles.saveBtn}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div
        style={{ ...styles.modal, maxWidth: 780 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>⚡ Bulk Add People</h2>
            <p style={{ color: "#7a7a7e", fontSize: 13, marginTop: 2 }}>
              Quickly add multiple people. Press Enter to advance.
            </p>
          </div>
          <button onClick={onClose} style={styles.modalClose}>
            ✕
          </button>
        </div>

        <div style={styles.modalBody}>
          {/* Column headers */}
          <div style={styles.bulkHeaderRow}>
            <span style={{ ...styles.bulkColLabel, flex: 2 }}>Name *</span>
            <span style={{ ...styles.bulkColLabel, flex: 2 }}>
              Relationship
            </span>
            <span style={{ ...styles.bulkColLabel, flex: 1.5 }}>Category</span>
            <span style={{ ...styles.bulkColLabel, flex: 1.5 }}>Link to</span>
            <span style={{ width: 32 }} />
          </div>

          {/* Rows */}
          <div style={styles.bulkRowsContainer}>
            {rows.map((row, idx) => {
              const linkOpts = getLinkOptions(row._key);
              return (
                <div
                  key={row._key}
                  style={{
                    ...styles.bulkRow,
                    ...(idx % 2 === 0 ? {} : { background: "#161618" }),
                  }}
                >
                  <input
                    ref={(el) => {
                      nameRefs.current[row._key] = el;
                    }}
                    style={{ ...styles.bulkInput, flex: 2 }}
                    value={row.name}
                    onChange={(e) =>
                      updateRow(row._key, "name", e.target.value)
                    }
                    onKeyDown={(e) => handleKeyDown(e, row._key, "name")}
                    placeholder={`Person ${idx + 1}`}
                  />
                  <input
                    id={`bulk-rel-${row._key}`}
                    style={{ ...styles.bulkInput, flex: 2 }}
                    value={row.relationship}
                    onChange={(e) =>
                      updateRow(row._key, "relationship", e.target.value)
                    }
                    onKeyDown={(e) =>
                      handleKeyDown(e, row._key, "relationship")
                    }
                    placeholder="e.g. Coworker"
                  />
                  <select
                    style={{ ...styles.bulkInput, flex: 1.5 }}
                    value={row.category}
                    onChange={(e) =>
                      updateRow(row._key, "category", e.target.value)
                    }
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.icon} {c.label}
                      </option>
                    ))}
                  </select>
                  <select
                    style={{ ...styles.bulkInput, flex: 1.5 }}
                    value={row.parentId}
                    onChange={(e) =>
                      updateRow(row._key, "parentId", e.target.value)
                    }
                  >
                    <option value="">— None —</option>
                    {linkOpts.existing.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                    {linkOpts.pending.length > 0 && (
                      <optgroup label="From this batch">
                        {linkOpts.pending.map((r) => (
                          <option key={r._key} value={`__pending_${r._key}`}>
                            {r.name} (new)
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <button
                    onClick={() => removeRow(row._key)}
                    style={styles.bulkRemoveBtn}
                    title="Remove row"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>

          <button onClick={addRow} style={styles.bulkAddRowBtn}>
            + Add another row
          </button>
        </div>

        <div style={{ ...styles.modalFooter, justifyContent: "space-between" }}>
          <span style={{ fontSize: 13, color: "#7a7a7e" }}>
            {validRows.length} {validRows.length === 1 ? "person" : "people"}{" "}
            ready to add
          </span>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} style={styles.cancelBtn}>
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={validRows.length === 0 || saving}
              style={styles.saveBtn}
            >
              {saving
                ? "Saving..."
                : `Add ${validRows.length} ${validRows.length === 1 ? "Person" : "People"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── PROFILE VIEW (Full Page) ────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
